import { WEEKDAYS, type Weekday } from '@/modules/products/passes';
import type { MvpDefinition, MvpOptionInput, MvpValidityUnit } from './definitions';

/**
 * The editor's in-progress state for one Multi-Visit Pass Definition. Numeric fields stay the
 * strings the user typed so validation can refuse "1.5" hours rather than silently rounding it.
 * `weekdays` null = every day; an empty array = "Every day" switched off with no day picked,
 * which the wire cannot express (empty = every day) and so is refused here.
 */
export interface MvpOptionDraft {
  key: string;
  id?: string;
  name: string;
  hours: string;
  price: string;
  validityAmount: string;
  validityUnit: MvpValidityUnit;
  weekdays: Weekday[] | null;
  active: boolean;
  soldCount: number;
}

export interface MvpDefinitionDraft {
  name: string;
  waiverRequired: boolean;
  active: boolean;
  options: MvpOptionDraft[];
}

/** Field → i18n key of the message. */
export type OptionErrors = Partial<
  Record<'name' | 'hours' | 'price' | 'validity' | 'weekdays', string>
>;

let nextKey = 0;
function newKey(): string {
  nextKey += 1;
  return `new-${nextKey}`;
}

export function blankOption(): MvpOptionDraft {
  return {
    key: newKey(),
    name: '',
    hours: '',
    price: '',
    validityAmount: '6',
    validityUnit: 'MONTHS',
    weekdays: null,
    active: true,
    soldCount: 0,
  };
}

export function emptyDraft(): MvpDefinitionDraft {
  return { name: '', waiverRequired: false, active: true, options: [blankOption()] };
}

export function draftFrom(definition: MvpDefinition): MvpDefinitionDraft {
  return {
    name: definition.name,
    waiverRequired: definition.waiverRequired,
    active: definition.active,
    options: definition.options.map((option) => ({
      key: option.id,
      id: option.id,
      name: option.name,
      hours: String(option.hours),
      price: option.price,
      validityAmount: String(option.validity.amount),
      validityUnit: option.validity.unit,
      weekdays: option.validWeekdays.length === 0 ? null : option.validWeekdays,
      active: option.active,
      soldCount: option.soldCount,
    })),
  };
}

const WHOLE = /^\d+$/;
const DECIMAL = /^\d+(\.\d+)?$/;

function wholeAtLeastOne(raw: string): boolean {
  return WHOLE.test(raw.trim()) && Number(raw) >= 1;
}

function positivePrice(raw: string): boolean {
  return DECIMAL.test(raw.trim()) && Number(raw) > 0;
}

/** The per-row rules the backend enforces, plus the frontend-only "no weekday chosen" rule. */
export function optionErrors(option: MvpOptionDraft, siblings: MvpOptionDraft[]): OptionErrors {
  const errors: OptionErrors = {};
  const name = option.name.trim().toLowerCase();
  if (!name) errors.name = 'multiVisitPasses.validation.optionNameRequired';
  else if (
    option.active &&
    siblings.some(
      (other) =>
        other.key !== option.key && other.active && other.name.trim().toLowerCase() === name,
    )
  )
    errors.name = 'multiVisitPasses.validation.optionNameDuplicate';
  if (!wholeAtLeastOne(option.hours)) errors.hours = 'multiVisitPasses.validation.hours';
  if (!positivePrice(option.price)) errors.price = 'multiVisitPasses.validation.price';
  if (!wholeAtLeastOne(option.validityAmount))
    errors.validity = 'multiVisitPasses.validation.validity';
  if (option.weekdays !== null && option.weekdays.length === 0)
    errors.weekdays = 'multiVisitPasses.validation.weekdays';
  return errors;
}

/** Definition-level errors as i18n keys; empty when the draft may be saved. */
export function definitionErrors(draft: MvpDefinitionDraft): string[] {
  const errors: string[] = [];
  if (!draft.name.trim()) errors.push('multiVisitPasses.validation.nameRequired');
  if (draft.options.length === 0) errors.push('multiVisitPasses.validation.optionsRequired');
  return errors;
}

export function isSavable(draft: MvpDefinitionDraft): boolean {
  return (
    definitionErrors(draft).length === 0 &&
    draft.options.every((option) => Object.keys(optionErrors(option, draft.options)).length === 0)
  );
}

/** The option rows as the API takes them. Every day (null or all seven) rides as an empty list. */
export function optionInputs(draft: MvpDefinitionDraft): MvpOptionInput[] {
  return draft.options.map((option) => ({
    id: option.id,
    name: option.name.trim(),
    hours: Number(option.hours),
    price: option.price.trim(),
    validity: { amount: Number(option.validityAmount), unit: option.validityUnit },
    validWeekdays:
      option.weekdays === null || option.weekdays.length === WEEKDAYS.length
        ? []
        : WEEKDAYS.filter((day) => option.weekdays!.includes(day)),
    active: option.active,
  }));
}

/** What the POS grid would show for this draft right now. */
export function optionsOnSale(draft: MvpDefinitionDraft): MvpOptionDraft[] {
  return draft.active ? draft.options.filter((option) => option.active) : [];
}

/** Price per hour as a two-decimal string, or null while hours or price are not yet valid. */
export function pricePerHour(option: Pick<MvpOptionDraft, 'hours' | 'price'>): string | null {
  if (!wholeAtLeastOne(option.hours) || !positivePrice(option.price)) return null;
  return (Number(option.price) / Number(option.hours)).toFixed(2);
}
